import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Stack, Container, Typography } from "@mui/material";
import FixtureFeedsTable from "../components/fixture-feeds/FixtureFeedsTable";
import { get } from "src/utils/networkUtils";
import useWithUrlTenant from "src/hooks/useTenant";

export default function FixtureFeedsPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [feeds, setFeeds] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);
  const [externalId, setExternalId] = useState(null);
  const withTenant = useWithUrlTenant();

  const fetchFeeds = useCallback(
    async (currentOffset = 0, isInitial = false) => {
      console.log("externalId", externalId);
      if (isInitial) {
        setLoading(true);
      }

      try {
        let response;
        if (searchParams.has("external_id")) {
          let externalId = searchParams.get("external_id");
          setExternalId(externalId);
          // Fetch feeds for a specific match
          response = await get(
            withTenant(
              `/admin/fixtures/match/${externalId}?offset=${currentOffset}&limit=${limit}`
            )
          );
        } else {
          // Fetch all feeds
          response = await get(
            withTenant(
              `/admin/fixtures/feeds?offset=${currentOffset}&limit=${limit}`
            )
          );
        }

        const newFeeds = response.feeds || [];

        if (isInitial) {
          setFeeds(newFeeds);
        } else {
          setFeeds((prev) => [...prev, ...newFeeds]);
        }

        setTotalCount(response.totalCount || 0);
        setHasMore(newFeeds.length === limit);
        setOffset(currentOffset + limit);
      } catch (error) {
        console.error("Error fetching fixture feeds:", error);
        if (isInitial) {
          setFeeds([]);
        }
      } finally {
        if (isInitial) {
          setLoading(false);
        }
      }
    },
    [limit, externalId, searchParams, withTenant]
  );

  const handleLoadMore = useCallback(() => {
    if (!hasMore) return;
    return fetchFeeds(offset);
  }, [fetchFeeds, offset, hasMore]);

  useEffect(() => {
    setFeeds([]);
    setOffset(0);
    setHasMore(true);
    fetchFeeds(0, true);
  }, [fetchFeeds]);

  return (
    <>
      <Helmet>
        <title>
          {externalId ? `Match Feeds - ${externalId}` : "All Fixture Feeds"}
        </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4" gutterBottom>
            {externalId ? `Match Feeds - ${externalId}` : "All Fixture Feeds"}
          </Typography>
        </Stack>

        <FixtureFeedsTable
          loading={loading}
          feeds={feeds}
          totalCount={totalCount}
          onLoadMore={handleLoadMore}
          hasMore={hasMore}
        />
      </Container>
    </>
  );
}
